import React from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Form,
  Badge,
} from "react-bootstrap";
import Carousel from "react-bootstrap/Carousel";

type Slide = {
  id: string;
  title: string;
  caption?: string;
  imgUrl: string;
  alt: string;
};

type Testimonial = {
  id: string;
  quote: string;
  author: string;
};

const heroSlides: Slide[] = [
  {
    id: "hero-1",
    title: "Bring Your Home Back to Life",
    caption: "Professional, insured, and eco-friendly power washing.",
    imgUrl:
      "https://images.unsplash.com/photo-1557800636-894a64c1696f?q=80&w=1600&auto=format&fit=crop",
    alt: "Pressure washing a driveway",
  },
  {
    id: "hero-2",
    title: "Restore Curb Appeal in Hours",
    caption: "Driveways, siding, decks, patios, and fences.",
    imgUrl:
      "https://images.unsplash.com/photo-1581094794329-c8112a89af12?q=80&w=1600&auto=format&fit=crop",
    alt: "Clean home exterior after wash",
  },
  {
    id: "hero-3",
    title: "Safe on Surfaces. Tough on Grime.",
    caption: "Soft-wash and power wash options tailored to your property.",
    imgUrl:
      "https://images.unsplash.com/photo-1523419409543-a1298f1a02e1?q=80&w=1600&auto=format&fit=crop",
    alt: "Deck being cleaned",
  },
];

const beforeAfterSlides: Slide[] = [
  {
    id: "ba-1",
    title: "Driveway — Before / After",
    caption: "Oil stains and grime removed.",
    imgUrl:
      "https://images.unsplash.com/photo-1597004638071-9f6a1c1b1f70?q=80&w=1400&auto=format&fit=crop",
    alt: "Driveway cleaned",
  },
  {
    id: "ba-2",
    title: "Siding — Before / After",
    caption: "Mold and mildew eliminated.",
    imgUrl:
      "https://images.unsplash.com/photo-1484154218962-a197022b5858?q=80&w=1400&auto=format&fit=crop",
    alt: "House siding cleaned",
  },
  {
    id: "ba-3",
    title: "Deck — Before / After",
    caption: "Wood brightened and refreshed.",
    imgUrl:
      "https://images.unsplash.com/photo-1523419409543-a1298f1a02e1?q=80&w=1400&auto=format&fit=crop",
    alt: "Deck after power washing",
  },
];

const testimonials: Testimonial[] = [
  {
    id: "t1",
    quote:
      "My driveway looks brand new. Fast, friendly, and affordable service!",
    author: "Sarah T.",
  },
  {
    id: "t2",
    quote: "They removed years of grime from our siding in one afternoon.",
    author: "James R.",
  },
  {
    id: "t3",
    quote: "The deck is spotless—great attention to detail. Highly recommend.",
    author: "Linda W.",
  },
];

const serviceCards = [
  {
    id: "svc-1",
    title: "House Washing",
    text:
      "Remove dirt, mold, and algae safely with our soft-wash process.",
    img: "https://images.unsplash.com/photo-1512918581190-980c2a84bb02?q=80&w=1200&auto=format&fit=crop",
  },
  {
    id: "svc-2",
    title: "Driveway & Concrete",
    text: "Cut through oil, rust, and tire marks for a fresh look.",
    img: "https://images.unsplash.com/photo-1541976590-713941681591?q=80&w=1200&auto=format&fit=crop",
  },
  {
    id: "svc-3",
    title: "Decks & Fences",
    text: "Revive wood and composite surfaces—ready for stain or seal.",
    img: "https://images.unsplash.com/photo-1523419409543-a1298f1a02e1?q=80&w=1200&auto=format&fit=crop",
  },
];

export const PowerWashLandingCarousel_v01: React.FC = () => {
  return (
    <div className="bg-light">
      {/* HERO CAROUSEL */}
      <section className="position-relative">
        <Carousel fade controls indicators interval={5500}>
          {heroSlides.map((s) => (
            <Carousel.Item key={s.id}>
              <div
                style={{
                  height: 520,
                  backgroundImage: `linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.45)), url(${s.imgUrl})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <Carousel.Caption className="text-start">
                <Badge bg="success" className="mb-2">
                  Licensed & Insured
                </Badge>
                <h1 className="display-5 fw-bold">{s.title}</h1>
                {s.caption && <p className="lead">{s.caption}</p>}
                <Button size="lg" variant="primary" className="mt-2">
                  Get a Free Quote
                </Button>
              </Carousel.Caption>
            </Carousel.Item>
          ))}
        </Carousel>
      </section>

      {/* TRUST BAR */}
      <section className="py-3 bg-white border-bottom">
        <Container className="text-center">
          <Row className="g-3">
            <Col xs={12} md={4}>
              <strong>Eco-Friendly Detergents</strong>
            </Col>
            <Col xs={12} md={4}>
              <strong>Satisfaction Guaranteed</strong>
            </Col>
            <Col xs={12} md={4}>
              <strong>Locally Owned & Operated</strong>
            </Col>
          </Row>
        </Container>
      </section>

      {/* SERVICES */}
      <section className="py-5">
        <Container>
          <h2 className="text-center mb-4">Our Services</h2>
          <Row className="g-4">
            {serviceCards.map((svc) => (
              <Col md={4} key={svc.id}>
                <Card className="h-100 shadow-sm">
                  <Card.Img src={svc.img} alt={svc.title} />
                  <Card.Body>
                    <Card.Title>{svc.title}</Card.Title>
                    <Card.Text>{svc.text}</Card.Text>
                    <Button variant="outline-primary" size="sm">
                      Learn More
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* BEFORE & AFTER (Carousel) */}
      <section className="py-5 bg-white">
        <Container>
          <h2 className="text-center mb-4">Before & After</h2>
          <Row className="justify-content-center">
            <Col md={10} lg={8}>
              <Carousel interval={6000} controls indicators>
                {beforeAfterSlides.map((s) => (
                  <Carousel.Item key={s.id}>
                    <img
                      className="d-block w-100"
                      src={s.imgUrl}
                      alt={s.alt}
                      style={{ maxHeight: 500, objectFit: "cover" }}
                    />
                    <Carousel.Caption>
                      <h5 className="fw-bold">{s.title}</h5>
                      {s.caption && <p>{s.caption}</p>}
                    </Carousel.Caption>
                  </Carousel.Item>
                ))}
              </Carousel>
            </Col>
          </Row>
        </Container>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-5">
        <Container>
          <h2 className="text-center mb-4">What Customers Say</h2>
          <Row className="g-4">
            {testimonials.map((t) => (
              <Col md={4} key={t.id}>
                <Card className="h-100 p-3 shadow-sm">
                  <Card.Text className="mb-2">
                    <span aria-hidden>⭐️⭐️⭐️⭐️⭐️</span>
                  </Card.Text>
                  <Card.Text className="fst-italic">“{t.quote}”</Card.Text>
                  <Card.Footer className="bg-transparent border-0 text-end">
                    — <strong>{t.author}</strong>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* QUOTE FORM */}
      <section className="py-5 bg-primary text-light">
        <Container>
          <h2 className="text-center mb-4">Request a Free Quote</h2>
          <Row className="justify-content-center">
            <Col md={6}>
              <Card className="shadow-lg">
                <Card.Body>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const form = e.currentTarget as HTMLFormElement;
                      const data = new FormData(form);
                      const payload = Object.fromEntries(data.entries());
                      // Test mode: log the payload
                      console.log("Quote Request:", payload);
                      alert("Thanks! We’ll be in touch shortly.");
                      form.reset();
                    }}
                  >
                    <Form.Group className="mb-3" controlId="name">
                      <Form.Label>Name</Form.Label>
                      <Form.Control
                        name="name"
                        type="text"
                        placeholder="Jane Doe"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="phone">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        name="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="email">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="service">
                          <Form.Label>Service</Form.Label>
                          <Form.Select name="service" required defaultValue="">
                            <option value="" disabled>
                              Choose…
                            </option>
                            <option>House Washing</option>
                            <option>Driveway & Concrete</option>
                            <option>Decks & Fences</option>
                            <option>Patios & Pavers</option>
                            <option>Gutters</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="zip">
                          <Form.Label>ZIP</Form.Label>
                          <Form.Control
                            name="zip"
                            type="text"
                            placeholder="80202"
                            pattern="\d{5}"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3" controlId="notes">
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="notes"
                        rows={3}
                        placeholder="Tell us about stains, surfaces, or timing…"
                      />
                    </Form.Group>

                    <div className="d-grid">
                      <Button variant="primary" type="submit" size="lg">
                        Get My Quote
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FOOTER */}
      <footer className="bg-dark text-light text-center p-3">
        <p className="m-0">
          © {new Date().getFullYear()} BrightWash Power Washing · Serving
          Denver & Surrounding Areas
        </p>
      </footer>
    </div>
  );
};

export default PowerWashLandingCarousel_v01;
